<template>
  <div class="datenschutz-page">
    <!-- Navigation -->
    <nav class="nav">
      <div class="nav-content">
        <div class="logo-container">
          <svg class="logo-svg" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
              </linearGradient>
            </defs>
            <path d="M 15 10 L 25 10 L 35 25 L 25 40 L 15 40 L 25 25 Z" fill="url(#logoGradient)"/>
            <circle cx="40" cy="15" r="3" fill="url(#logoGradient)"/>
            <circle cx="40" cy="35" r="3" fill="url(#logoGradient)"/>
          </svg>
          <div class="logo-text">
            <span class="logo-name">{{ companyName }}</span>
            <span class="logo-subtitle">{{ companySubtitle }}</span>
          </div>
        </div>
        <a href="/" class="back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Zurück zur Startseite
        </a>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-background">
        <div class="hero-grid"></div>
      </div>
      <div class="hero-content">
        <div class="hero-tag">{{ heroTag }}</div>
        <h1 class="hero-title">{{ heroTitle }}</h1>
        <p class="hero-subtitle">{{ heroSubtitle }}</p>
      </div>
    </section>

    <!-- Content Section -->
    <section class="content-section">
      <div class="content-container">
        <div class="content-card">
          
          <!-- Dynamic Sections -->
          <div v-for="section in sections" :key="section.id" class="section-block">
            <div class="section-header">
              <div class="section-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path v-if="section.icon.path" :d="section.icon.path"/>
                  <circle v-if="section.icon.circle" :cx="section.icon.circle.cx" :cy="section.icon.circle.cy" :r="section.icon.circle.r"/>
                  <rect v-if="section.icon.rect" :x="section.icon.rect.x" :y="section.icon.rect.y" :width="section.icon.rect.width" :height="section.icon.rect.height" :rx="section.icon.rect.rx"/>
                  <line v-for="(line, idx) in section.icon.lines" :key="idx" :x1="line.x1" :y1="line.y1" :x2="line.x2" :y2="line.y2"/>
                  <polyline v-if="section.icon.polyline" :points="section.icon.polyline"/>
                </svg>
              </div>
              <h2 class="section-title">{{ section.title }}</h2>
            </div>
            <div class="section-content">
              <p v-for="(paragraph, idx) in section.content" :key="idx" v-html="paragraph"></p>
              
              <!-- Rights List -->
              <ul v-if="section.rights" class="rights-list">
                <li v-for="right in section.rights" :key="right.title" class="right-item">
                  <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span><strong>{{ right.title }}</strong> {{ right.description }}</span>
                </li>
              </ul>

              <!-- Authority Info -->
              <div v-if="section.authority" class="authority-info">
                <p class="authority-name">{{ section.authority.name }}</p>
                <p v-for="(line, idx) in section.authority.address" :key="idx">{{ line }}</p>
                <p v-if="section.authority.phone">Telefon: {{ section.authority.phone }}</p>
                <p v-if="section.authority.email">
                  E-Mail: <a :href="`mailto:${section.authority.email}`">{{ section.authority.email }}</a>
                </p>
                <p v-if="section.authority.website">
                  <a :href="section.authority.website" target="_blank" rel="noopener noreferrer">
                    {{ section.authority.website }}
                  </a>
                </p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="footer-note">
            <p class="footer-date">{{ footerDate }}</p>
            <p class="footer-copy">{{ footerText }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Back to Top Button -->
    <button @click="scrollToTop" class="back-to-top" aria-label="Nach oben scrollen">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 19V5M5 12l7-7 7 7"/>
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'DatenschutzPage',
  data() {
    return {
      companyName: 'Nick Volkmann',
      companySubtitle: 'IT-Beratung',
      heroTag: 'Datenschutz',
      heroTitle: 'Datenschutzerklärung',
      heroSubtitle: 'Informationen zur Verarbeitung Ihrer personenbezogenen Daten',
      
      sections: [
        {
          id: 'general',
          title: 'Allgemeine Hinweise',
          icon: {
            circle: { cx: 12, cy: 12, r: 10 },
            lines: [
              { x1: 12, y1: 8, x2: 12, y2: 12 },
              { x1: 12, y1: 16, x2: 12.01, y2: 16 }
            ]
          },
          content: [
            'Diese Datenschutzerklärung enthält ausführliche Informationen darüber, was mit Ihren persönlichen Daten passiert, wenn Sie unsere Website <a href="https://www.nick-volkmann.de" class="link">www.nick-volkmann.de</a> besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie sich persönlich identifizieren können. Wir halten uns bei der Verarbeitung Ihrer Daten streng an die gesetzlichen Bestimmungen, insbesondere die Datenschutzgrundverordnung (DSGVO).'
          ]
        },
        {
          id: 'responsible',
          title: 'Verantwortliche Stelle',
          icon: {
            path: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2',
            circle: { cx: 12, cy: 7, r: 4 }
          },
          content: [
            '<span class="highlight">Nick Volkmann - IT-Beratung</span>',
            'Bitte erforderliche Adresse einfügen',
            'Deutschland',
            'E-Mail: <a href="mailto:info@nick-volkmann.de" class="link">info@nick-volkmann.de</a>',
            'Tel.: Bitte erforderliche Telefonnummer einfügen'
          ]
        },
        {
          id: 'server-logs',
          title: 'Zugriffsdaten (Server-Logfiles)',
          icon: {
            rect: { x: 2, y: 3, width: 20, height: 14, rx: 2 },
            lines: [
              { x1: 8, y1: 21, x2: 16, y2: 21 },
              { x1: 12, y1: 17, x2: 12, y2: 21 }
            ]
          },
          content: [
            'Beim Aufruf unserer Website speichern wir automatisch sogenannte Server-Logfiles wie Browsertyp, Datum, Uhrzeit, IP-Adresse (anonymisiert) gemäß Art. 6 Abs. 1 lit. f DSGVO. Diese Daten werden ausschließlich zur Sicherstellung eines reibungslosen Verbindungsaufbaus, zur Gewährleistung einer komfortablen Nutzung unserer Website sowie zur Auswertung der Systemsicherheit und -stabilität verwendet.'
          ]
        },
        {
          id: 'cookies',
          title: 'Cookies',
          icon: {
            circle: { cx: 12, cy: 12, r: 10 },
            lines: [
              { x1: 12, y1: 2, x2: 12, y2: 4 },
              { x1: 12, y1: 20, x2: 12, y2: 22 },
              { x1: 4.93, y1: 4.93, x2: 6.34, y2: 6.34 },
              { x1: 17.66, y1: 17.66, x2: 19.07, y2: 19.07 }
            ]
          },
          content: [
            'Unsere Website verwendet Cookies zur Optimierung und Sicherstellung der Funktionalität. Cookies sind kleine Textdateien, die auf Ihrem Endgerät gespeichert werden und die Ihr Browser speichert.',
            'Diese Verarbeitung erfolgt gemäß Art. 6 Abs. 1 lit. f DSGVO auf Basis unseres berechtigten Interesses an einer nutzerfreundlichen Gestaltung unserer Website. Sie können Cookies jederzeit in Ihrem Browser deaktivieren. Bitte beachten Sie, dass dadurch möglicherweise nicht alle Funktionen unserer Website zur Verfügung stehen.'
          ]
        },
        {
          id: 'google-maps',
          title: 'Google Maps',
          icon: {
            path: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z',
            circle: { cx: 12, cy: 10, r: 3 }
          },
          content: [
            'Diese Website nutzt Google Maps zur Darstellung interaktiver Karten, jedoch nur nach Ihrer ausdrücklichen Zustimmung (Art. 6 Abs. 1 lit. a DSGVO). Erst nach Aktivierung wird Ihre IP-Adresse an Google übermittelt.',
            'Weitere Informationen zum Datenschutz von Google finden Sie unter: <a href="https://www.google.de/intl/de/policies/privacy/" class="link" target="_blank" rel="noopener noreferrer">Google Datenschutzerklärung</a>'
          ]
        },
        {
          id: 'newsletter',
          title: 'Newsletter',
          icon: {
            rect: { x: 2, y: 4, width: 20, height: 16, rx: 2 },
            path: 'm22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7'
          },
          content: [
            'Ihre E-Mail-Adresse wird ausschließlich zum Versand unseres Newsletters verwendet, basierend auf Ihrer ausdrücklichen Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO. Sie können sich jederzeit vom Newsletter abmelden. Einen Abmeldelink finden Sie in jedem Newsletter.'
          ]
        },
        {
          id: 'contact',
          title: 'Kontaktaufnahme',
          icon: {
            path: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'
          },
          content: [
            'Ihre über das Kontaktformular oder per E-Mail übermittelten Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet. Die Verarbeitung basiert auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Die Daten werden gelöscht, sobald sie für die Erreichung des Zwecks ihrer Erhebung nicht mehr erforderlich sind.'
          ]
        },
        {
          id: 'user-account',
          title: 'Nutzerkonto',
          icon: {
            path: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
            circle: { cx: 9, cy: 7, r: 4 },
            lines: [
              { x1: 19, y1: 8, x2: 19, y2: 14 },
              { x1: 22, y1: 11, x2: 16, y2: 11 }
            ]
          },
          content: [
            'Mit der Erstellung eines Nutzerkontos willigen Sie in die Speicherung Ihrer Bestands- und Nutzungsdaten ein. Sie können Ihr Nutzerkonto jederzeit löschen lassen. Wenden Sie sich hierzu an die im Impressum angegebene Adresse.'
          ]
        },
        {
          id: 'data-usage',
          title: 'Datenverwendung und -weitergabe',
          icon: {
            path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'
          },
          content: [
            'Ihre Daten werden nicht an Dritte weitergegeben, außer zur Zahlungsabwicklung (z.B. PayPal, Stripe) oder sofern gesetzlich erforderlich. Die Weitergabe erfolgt ausschließlich im Rahmen der Vertragserfüllung und auf Basis datenschutzrechtlicher Vereinbarungen.'
          ]
        },
        {
          id: 'ssl',
          title: 'SSL- bzw. TLS-Verschlüsselung',
          icon: {
            rect: { x: 3, y: 11, width: 18, height: 11, rx: 2 },
            path: 'M7 11V7a5 5 0 0 1 10 0v4'
          },
          content: [
            'Unsere Website nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine SSL- bzw. TLS-Verschlüsselung. Dies erkennen Sie an der Zeichenfolge "https://" und dem Schloss-Symbol in Ihrer Browserzeile. Eine verschlüsselte Verbindung verhindert, dass übermittelte Daten von Dritten mitgelesen werden können.'
          ]
        },
        {
          id: 'storage',
          title: 'Speicherdauer',
          icon: {
            circle: { cx: 12, cy: 12, r: 10 },
            polyline: '12 6 12 12 16 14'
          },
          content: [
            'Personenbezogene Daten speichern wir nur so lange, wie es zur Zweckerfüllung notwendig ist, oder entsprechend gesetzlicher Aufbewahrungsfristen (z.B. handels- und steuerrechtliche Aufbewahrungspflichten). Nach Ablauf dieser Fristen werden die Daten routinemäßig gelöscht.'
          ]
        },
        {
          id: 'rights',
          title: 'Ihre Rechte',
          icon: {
            path: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'
          },
          content: [
            'Sie haben gemäß der DSGVO folgende Rechte in Bezug auf Ihre personenbezogenen Daten:'
          ],
          rights: [
            { title: 'Widerrufsrecht', description: '(Art. 7 Abs. 3 DSGVO) - Erteilte Einwilligungen können Sie jederzeit widerrufen' },
            { title: 'Auskunftsrecht', description: '(Art. 15 DSGVO) - Auskunft über Ihre gespeicherten Daten' },
            { title: 'Recht auf Berichtigung', description: '(Art. 16 DSGVO) - Korrektur unrichtiger Daten' },
            { title: 'Recht auf Löschung', description: '(Art. 17 DSGVO) - "Recht auf Vergessenwerden"' },
            { title: 'Recht auf Einschränkung der Verarbeitung', description: '(Art. 18 DSGVO)' },
            { title: 'Recht auf Datenübertragbarkeit', description: '(Art. 20 DSGVO)' },
            { title: 'Widerspruchsrecht', description: '(Art. 21 DSGVO)' },
            { title: 'Beschwerderecht bei der Aufsichtsbehörde', description: '(Art. 77 DSGVO)' }
          ]
        },
        {
          id: 'authority',
          title: 'Zuständige Aufsichtsbehörde',
          icon: {
            path: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
            polyline: '9 22 9 12 15 12 15 22'
          },
          content: [],
          authority: {
            name: 'Der Sächsische Datenschutzbeauftragte',
            address: [
              'Devrientstraße 5, 01067 Dresden',
              'Postfach 11 01 32, 01330 Dresden'
            ],
            phone: '0351 / 85471-101',
            email: 'saechsdsb@slt.sachsen.de',
            website: 'https://www.saechsdsb.de'
          }
        }
      ],
      
      footerText: '© 2025 Nick Volkmann - IT-Beratung. Alle Rechte vorbehalten.'
    }
  },
  computed: {
    footerDate() {
      const now = new Date();
      return `Diese Datenschutzerklärung gilt ab dem ${now.toLocaleDateString('de-DE', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      })}.`;
    }
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.datenschutz-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  line-height: 1.6;
  color: #1a202c;
  background: #fff;
  min-height: 100vh;
}

/* Navigation */
.nav {
  position: fixed;
  top: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  z-index: 50;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.nav-content {
  max-width: 1152px;
  margin: 0 auto;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-svg {
  width: 2.5rem;
  height: 2.5rem;
}

.logo-text {
  display: flex;
  flex-direction: column;
}

.logo-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.logo-subtitle {
  font-size: 0.75rem;
  color: #4b5563;
  font-weight: 500;
}

.back-link {
  color: #374151;
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.back-link:hover {
  color: #7c3aed;
}

/* Hero Section */
.hero {
  position: relative;
  padding: 128px 24px 80px;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: 0;
}

.hero-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.1;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
}

.hero-tag {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: white;
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 24px;
}

.hero-title {
  font-size: 56px;
  font-weight: 800;
  color: white;
  margin-bottom: 24px;
  line-height: 1.1;
}

.hero-subtitle {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  line-height: 1.7;
}

/* Content Section */
.content-section {
  padding: 80px 24px;
  background: #f8fafc;
}

.content-container {
  max-width: 900px;
  margin: 0 auto;
}

.content-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
  padding: 48px;
}

.section-block {
  margin-bottom: 48px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.section-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
}

.section-content {
  margin-left: 60px;
  color: #4a5568;
  line-height: 1.8;
}

.section-content p {
  margin-bottom: 12px;
}

.section-content .highlight {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  display: block;
  margin-bottom: 8px;
}

.section-content .link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
}

.section-content .link:hover {
  color: #764ba2;
}

/* Rights List */
.rights-list {
  list-style: none;
  margin-top: 16px;
}

.right-item {
  display: flex;
  align-items: start;
  gap: 12px;
  margin-bottom: 12px;
}

.check-icon {
  width: 20px;
  height: 20px;
  color: #667eea;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Authority Info */
.authority-info {
  margin-top: 16px;
}

.authority-info p {
  margin-bottom: 8px;
}

.authority-name {
  font-weight: 600;
  color: #1a202c !important;
  margin-bottom: 12px !important;
}

.authority-info a {
  color: #667eea;
  text-decoration: none;
  transition: color 0.3s;
}

.authority-info a:hover {
  color: #764ba2;
}

/* Footer */
.footer-note {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  font-size: 14px;
  color: #718096;
}

.footer-date {
  margin-bottom: 8px;
}

.footer-copy {
  margin-top: 8px;
}

/* Back to Top Button */
.back-to-top {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  z-index: 999;
}

.back-to-top:hover {
  transform: translateY(-4px) scale(1.1);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.5);
}

.back-to-top svg {
  transition: transform 0.3s;
}

.back-to-top:hover svg {
  transform: translateY(-2px);
}

/* Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 36px;
  }

  .hero-subtitle {
    font-size: 18px;
  }

  .content-card {
    padding: 32px 24px;
  }

  .section-content {
    margin-left: 0;
    margin-top: 16px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .back-to-top {
    bottom: 24px;
    right: 24px;
  }

  .nav-content {
    padding: 16px;
  }

  .back-link span {
    display: none;
  }
}
</style>